import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { usePreviewContext } from '@/components/PreviewProvider';

interface LocalizedImage {
  imageUrl: string | null;
  altText: string | null;
  loading: boolean;
}
// Inject a <link rel="preload"> for hero images so the browser starts
// downloading them before React renders the <img> tag.
const preloadedUrls = new Set<string>();
function preloadImage(url: string) {
  if (!url || preloadedUrls.has(url) || typeof document === 'undefined') return;
  preloadedUrls.add(url);
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  document.head.appendChild(link);
}

const visualsCache: Map<string, any> = new Map();

export function clearVisualsCache() {
  visualsCache.clear();
}

export function useLocalizedImage(
  page: string, 
  section: string, 
  key: string, 
  fallbackImage?: string,
  options?: { preload?: boolean }
): LocalizedImage {
  const { i18n } = useTranslation();
  const { isPreview, visualDraftsMap } = usePreviewContext();
  const [imageUrl, setImageUrl] = useState<string | null>(fallbackImage || null);
  const [altText, setAltText] = useState<string | null>(null);
  const [loading, setLoading] = useState(!fallbackImage);

  const currentLang = i18n.language?.split('-')[0] || 'nl';
  const cacheKey = `${page}-${section}-${key}`;

  useEffect(() => {
    // In preview mode, check visual drafts first
    if (isPreview && visualDraftsMap.size > 0) {
      const draft = visualDraftsMap.get(cacheKey);
      if (draft) {
        const draftUrl = (draft as any)[`image_url_${currentLang}`];
        const draftAlt = (draft as any)[`alt_text_${currentLang}`];
        const resolvedUrl = draftUrl || (draft as any).image_url_nl || fallbackImage || null;
        const resolvedAlt = draftAlt || (draft as any).alt_text_nl || null;
        console.log(`[useLocalizedImage] Draft found for ${cacheKey}:`, resolvedUrl);
        if (options?.preload && resolvedUrl) preloadImage(resolvedUrl);
        setImageUrl(resolvedUrl);
        setAltText(resolvedAlt);
        setLoading(false);
        return;
      }
    }

    async function loadImage() {
      // Check cache first
      if (visualsCache.has(cacheKey)) {
        const cached = visualsCache.get(cacheKey);
        const langImageUrl = cached[`image_url_${currentLang}`];
        const langAltText = cached[`alt_text_${currentLang}`];
        const resolvedUrl = langImageUrl || cached.image_url_nl || fallbackImage || null;
        if (options?.preload && resolvedUrl) preloadImage(resolvedUrl);
        setImageUrl(resolvedUrl);
        setAltText(langAltText || cached.alt_text_nl || null);
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('visuals')
          .select('*')
          .eq('page', page)
          .eq('section', section)
          .eq('key', key)
          .maybeSingle();

        if (error) {
          console.error('Error loading localized image:', error);
          setImageUrl(fallbackImage || null);
          setLoading(false);
          return;
        }

        if (data) {
          visualsCache.set(cacheKey, data);
          const langImageUrl = data[`image_url_${currentLang}` as keyof typeof data] as string | null;
          const langAltText = data[`alt_text_${currentLang}` as keyof typeof data] as string | null;
          const resolvedUrl = langImageUrl || data.image_url_nl || fallbackImage || null;
          if (options?.preload && resolvedUrl) preloadImage(resolvedUrl);
          setImageUrl(resolvedUrl);
          setAltText(langAltText || data.alt_text_nl || null);
        } else {
          setImageUrl(fallbackImage || null);
        }
      } catch (err) {
        console.error('Error loading localized image:', err);
        setImageUrl(fallbackImage || null);
      }
      
      setLoading(false);
    }

    loadImage();
  }, [page, section, key, currentLang, fallbackImage, cacheKey, isPreview, visualDraftsMap]);

  return { imageUrl, altText, loading };
}

// Hook to load multiple images at once
export function useLocalizedImages(
  page: string,
  section: string,
  keys: string[],
  fallbackImages?: Record<string, string>
): Record<string, LocalizedImage> {
  const { i18n } = useTranslation();
  const { isPreview, visualDraftsMap } = usePreviewContext();
  const [images, setImages] = useState<Record<string, LocalizedImage>>(() => {
    const initial: Record<string, LocalizedImage> = {};
    keys.forEach(key => {
      initial[key] = {
        imageUrl: fallbackImages?.[key] || null,
        altText: null,
        loading: true,
      };
    });
    return initial;
  });

  const currentLang = i18n.language?.split('-')[0] || 'nl';

  useEffect(() => {
    async function loadImages() {
      try {
        const { data, error } = await supabase
          .from('visuals')
          .select('*')
          .eq('page', page)
          .eq('section', section)
          .in('key', keys);

        if (error) {
          console.error('Error loading localized images:', error);
          setImages(prev => {
            const updated = { ...prev };
            keys.forEach(key => {
              updated[key] = {
                imageUrl: fallbackImages?.[key] || null,
                altText: null,
                loading: false,
              };
            });
            return updated;
          });
          return;
        }

        const result: Record<string, LocalizedImage> = {};
        
        keys.forEach(key => {
          const ck = `${page}-${section}-${key}`;
          const visual = data?.find(v => v.key === key);
          
          if (visual) {
            visualsCache.set(ck, visual);
          }

          // In preview mode, draft overrides published
          if (isPreview && visualDraftsMap.size > 0) {
            const draft = visualDraftsMap.get(ck);
            if (draft) {
              const draftUrl = (draft as any)[`image_url_${currentLang}`];
              const draftAlt = (draft as any)[`alt_text_${currentLang}`];
              result[key] = {
                imageUrl: draftUrl || (draft as any).image_url_nl || fallbackImages?.[key] || null,
                altText: draftAlt || (draft as any).alt_text_nl || null,
                loading: false,
              };
              return; // skip published for this key
            }
          }

          const source = visual;
          if (source) {
            const langImageUrl = source[`image_url_${currentLang}` as keyof typeof source] as string | null;
            const langAltText = source[`alt_text_${currentLang}` as keyof typeof source] as string | null;
            
            result[key] = {
              imageUrl: langImageUrl || source.image_url_nl || fallbackImages?.[key] || null,
              altText: langAltText || source.alt_text_nl || null,
              loading: false,
            };
          } else {
            result[key] = {
              imageUrl: fallbackImages?.[key] || null,
              altText: null,
              loading: false,
            };
          }
        });

        setImages(result);
      } catch (err) {
        console.error('Error loading localized images:', err);
      }
    }

    loadImages();
  }, [page, section, keys.join(','), currentLang, isPreview, visualDraftsMap]);

  return images;
}
